/**
 * The Position Tracker bot view: TWO discrete sections — "My Bots" (claimed by
 * you) and "Org bots" (the pool you haven't) — that a bot moves between when you
 * claim or release it. Each section is the SAME rich race-board as the Bots page
 * (<FleetBoard>: fill anchor, heat ramp, full metric band, climb sparkline, claim
 * cell, controls, queue-ladder detail) and carries its OWN view mode (compact /
 * card / table) + open-row, so the two are managed independently. My = isMine,
 * Org = !isMine — exact complements over the fleet the relay returns. That fleet
 * IS org-scoped server-side now: a manager/trader gets only their own org's bots,
 * and an admin gets the org they've PICKED (selectedOrgId → ?orgId) plus any
 * unscoped/legacy bot. So this client split partitions an already-org-bounded set.
 *
 * Self-gating + self-contained — mount it once in the tracker. Renders nothing
 * unless the viewer can see bots (a bot-access trader, or a manager/admin), the
 * edge is configured, and at least one bot is running. Controls (claim/release,
 * pause/resume, stop) come from useEdgeFleet; mutating controls are gated by the
 * relay's server-computed canOperate (org membership; admin role does NOT bypass)
 * so none silently 403; reband is omitted (no edge endpoint here), so its
 * affordance hides. Stop is confirmed (one shared modal).
 */
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOrgScope } from '@/stores/orgScope';
import { useEdgeFleet } from '@/hooks/useEdgeFleet';
import { ViewModeToggle } from '@/components/cards/cardKit';
import { ConfirmModal } from '@/components/atoms/Modal';
import { FleetBoard, type FleetControls } from './FleetBoard';
import type { AgentStats } from '@/lib/bots/edge';
import type { Motion, ViewMode } from '@/lib/bots/telemetry';

export function BotZones() {
  const me = useAuthStore((s) => s.user?.username);
  const canView = useAuthStore((s) => {
    const u = s.user;
    if (!u) return false;
    return (u.botAccess ?? false) || u.role === 'manager' || u.role === 'admin';
  });
  const isPriv = useAuthStore((s) => s.user?.role === 'manager' || s.user?.role === 'admin');
  // An admin can VIEW another org's fleet by picking it (the relay honors ?orgId
  // for admins only; interaction stays membership-gated server-side).
  const selectedOrgId = useOrgScope((s) => s.selectedOrgId);
  const fleet = useEdgeFleet({ enabled: canView, orgId: selectedOrgId });
  const [killId, setKillId] = useState<string | null>(null);

  // No bot access → render nothing. But for someone who CAN see bots, an empty or
  // unconfigured lane should say WHY — otherwise "empty" reads as "no access."
  if (!canView) return null;
  if (fleet.status === 'loading') return null; // transient — don't flash a note
  if (fleet.status !== 'ok' || fleet.agents.length === 0) {
    const note =
      fleet.status === 'not-configured'
        ? 'Bot engine not configured on this lane.'
        : fleet.status === 'error'
          ? 'Bot feed unavailable right now.'
          : 'No bots running yet.';
    return (
      <div className="space-y-2">
        <h2 className="text-foreground text-sm font-semibold">Bots</h2>
        <div className="text-muted-foreground border-border rounded-md border border-dashed px-3 py-4 text-center text-xs">
          {note}
          {fleet.status === 'ok' && <span className="opacity-70"> Deploy one from the Value Dashboard.</span>}
        </div>
      </div>
    );
  }

  const isMine = (username?: string | null) => !!username && username === me;
  const mine = fleet.agents.filter((a) => isMine(a.owner?.username));
  const pool = fleet.agents.filter((a) => !isMine(a.owner?.username));

  // A trader may mutate only its own bots (the relay enforces owner-only authz);
  // managers/admins may mutate any. Gate pause/stop to match — a viewer never
  // sees a control that would just silently 403. Claim stays open to all.
  // Operate (pause/stop/reband) is gated by the relay's server-computed
  // canOperate (org-membership; admin role does NOT bypass — see edge-proxy).
  // Fallback to the old role/owner heuristic only if an older relay omitted it.
  const canMutate = (a: AgentStats) => a.canOperate ?? (isPriv || isMine(a.owner?.username));

  const controls: FleetControls = {
    pause: (id) => void fleet.pause(id),
    resume: (id) => void fleet.resume(id),
    kill: (id) => setKillId(id),
    claim: (id, ticker) => void fleet.claim(id, ticker),
    release: (id) => void fleet.release(id),
    // reband omitted — useEdgeFleet has no reband endpoint; the affordance hides.
  };

  return (
    <div className="space-y-5">
      <BotSection
        title="My Bots"
        help="Bots you've claimed. Their orders work in the background; once a bot takes a fill it becomes a position you own."
        agents={mine}
        motion={fleet.motion}
        defaultView="card"
        meUsername={me}
        controls={controls}
        canMutate={canMutate}
        emptyHint="No bots claimed by you. Claim one from the org pool below, or deploy from the Value Dashboard."
      />
      <BotSection
        title="Org bots"
        help="Bots running for your org that you haven't claimed (unclaimed, or held by a teammate). Claim one to take the wheel."
        agents={pool}
        motion={fleet.motion}
        defaultView="compact"
        meUsername={me}
        controls={controls}
        canMutate={canMutate}
        emptyHint="No bots in the org pool — every running bot is claimed by you."
      />

      <ConfirmModal
        isOpen={!!killId}
        onClose={() => setKillId(null)}
        onConfirm={() => {
          if (killId) void fleet.kill(killId);
          setKillId(null);
        }}
        title="Stop bot?"
        message="This cancels all of the bot's resting orders and removes it from the fleet."
        confirmLabel="Stop"
        variant="danger"
      />
    </div>
  );
}

/**
 * One discrete bot section (My Bots / Org bots) with its OWN view mode + open-row
 * state, so the two are managed independently. The header sits in the page
 * background (matches the Positions / Open Orders headers); the rich board is the
 * card beneath.
 */
function BotSection({
  title,
  help,
  agents,
  motion,
  defaultView,
  meUsername,
  controls,
  canMutate,
  emptyHint,
}: {
  title: string;
  help: string;
  agents: AgentStats[];
  motion: Map<string, Motion>;
  defaultView: ViewMode;
  meUsername?: string;
  controls: FleetControls;
  canMutate: (a: AgentStats) => boolean;
  emptyHint: string;
}) {
  const [view, setView] = useState<ViewMode>(defaultView);
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-foreground flex items-center gap-2 text-sm font-semibold">
          {title}
          <span className="text-muted-foreground text-xs font-normal">{agents.length}</span>
        </h2>
        <ViewModeToggle value={view} onChange={setView} modes={['compact', 'card', 'table'] as const} />
      </div>
      {help && <p className="text-muted-foreground text-[11px]">{help}</p>}
      <FleetBoard
        agents={agents}
        motion={motion}
        view={view}
        openId={openId}
        onOpen={(id) => setOpenId((x) => (x === id ? null : id))}
        meUsername={meUsername}
        controls={controls}
        canMutate={canMutate}
        emptyHint={emptyHint}
      />
    </div>
  );
}
